"use strict";
/**
 * AEM Layout Controller
 *
 * The goal of this composable is to enable communication between Vue components rendered through
 * the AEM editable components library so that they can agree on how components should be laid out
 * on the page, if one of the components decides it shouldn't be visible for any reason.
 *
 * It also controls hiding unnecessary AEM containers when a component shouldn't be rendered on the page,
 * an example is on the home page, the blue background surrounding the financial accounts component
 * should be removed when that component doesn't show.
 *
 * Components can control their visibility by using the composable:
 * ```
 * const aemLayoutController = useAemLayoutController();
 * aemLayoutController.hideComponent();
 * ```
 *
 * Components can listen to the visibility changes of AEM components by using the v-aem-layout-listener directive:
 * ```
 * <component
 *     :is="someImportedAemComponent"
 *     v-aem-layout-listener
 *     v-on-native.aem-layout-component-hidden="onComponentHidden" />
 * ```
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAemLayoutLocalController = exports.createAemLayoutController = void 0;
const vue_1 = require("vue");
const injection_symbols_1 = require("./injection-symbols");
/**
 * Creates the AEM layout controller composable instance, for use as a plugin in the Vue app.
 */
function createAemLayoutController() {
    /*
     * Notifies event listeners created by the v-aem-layout-listener directive.
     */
    function notifyListeners(currentEl, data) {
        const listenerEl = currentEl.closest('[data-aem-layout-controller-listener]');
        if (listenerEl) {
            const event = new CustomEvent(data.eventType, {
                detail: data
            });
            listenerEl.dispatchEvent(event);
            if (listenerEl.parentElement) {
                notifyListeners(listenerEl.parentElement, data);
            }
        }
    }
    /**
     * Hides the component with specified root element, and also hides the parent AEM container
     * if it is the only visible component in that container.
     */
    function hideComponent(componentEl, options) {
        var _a, _b, _c;
        const parentContainer = ((_a = componentEl.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('aem-container'))
            ? componentEl.parentElement : undefined;
        const grandparentContainer = ((_b = parentContainer === null || parentContainer === void 0 ? void 0 : parentContainer.parentElement) === null || _b === void 0 ? void 0 : _b.classList.contains('mhtk-container'))
            ? parentContainer.parentElement : undefined;
        componentEl.setAttribute('hidden', 'true');
        // Hide any empty sibling containers that AEM generated...
        for (const child of (_c = parentContainer === null || parentContainer === void 0 ? void 0 : parentContainer.children) !== null && _c !== void 0 ? _c : []) {
            if (child.classList.contains('mhtk-container')
                && child.children.length === 1
                && child.children[0].classList.contains('aem-container')
                && child.children[0].children.length === 0) {
                child.setAttribute('hidden', 'true');
            }
        }
        // Hide the parent "aem-container" if all of its children are hidden
        if (((options === null || options === void 0 ? void 0 : options.alwaysHideParentContainer)
            || (parentContainer === null || parentContainer === void 0 ? void 0 : parentContainer.querySelectorAll(':scope > [hidden]').length)
                === (parentContainer === null || parentContainer === void 0 ? void 0 : parentContainer.children.length)) && (options === null || options === void 0 ? void 0 : options.neverHideParentContainer) !== true) {
            parentContainer === null || parentContainer === void 0 ? void 0 : parentContainer.setAttribute('hidden', 'true');
        }
        // Hide the parent "mhtk-container" if all of its children are hidden
        if (((options === null || options === void 0 ? void 0 : options.alwaysHideParentContainer)
            || (grandparentContainer === null || grandparentContainer === void 0 ? void 0 : grandparentContainer.querySelectorAll(':scope > [hidden]').length)
                === (grandparentContainer === null || grandparentContainer === void 0 ? void 0 : grandparentContainer.children.length)) && (options === null || options === void 0 ? void 0 : options.neverHideParentContainer) !== true) {
            grandparentContainer === null || grandparentContainer === void 0 ? void 0 : grandparentContainer.setAttribute('hidden', 'true');
        }
        notifyListeners(componentEl, {
            eventType: 'aem-layout-component-hidden',
            visible: false
        });
        notifyListeners(componentEl, {
            eventType: 'aem-layout-component-toggled',
            visible: false
        });
    }
    /**
     * Shows the component with specified root element, and shows its parent AEM container if it is hidden.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function showComponent(componentEl, options) {
        var _a, _b;
        const parentContainer = ((_a = componentEl.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('aem-container'))
            ? componentEl.parentElement : undefined;
        const grandparentContainer = ((_b = parentContainer === null || parentContainer === void 0 ? void 0 : parentContainer.parentElement) === null || _b === void 0 ? void 0 : _b.classList.contains('mhtk-container'))
            ? parentContainer.parentElement : undefined;
        componentEl.removeAttribute('hidden');
        parentContainer === null || parentContainer === void 0 ? void 0 : parentContainer.removeAttribute('hidden');
        grandparentContainer === null || grandparentContainer === void 0 ? void 0 : grandparentContainer.removeAttribute('hidden');
        notifyListeners(componentEl, {
            eventType: 'aem-layout-component-shown',
            visible: true
        });
        notifyListeners(componentEl, {
            eventType: 'aem-layout-component-toggled',
            visible: true
        });
    }
    /**
     * Toggles the visibility of the component with specified root element,
     * and shows or hides its parent AEM container likewise.
     */
    function toggleComponent(componentEl, options) {
        let shouldBeVisible = options === null || options === void 0 ? void 0 : options.visible;
        if (shouldBeVisible == null) {
            shouldBeVisible = componentEl.hasAttribute('hidden');
        }
        if (shouldBeVisible) {
            showComponent(componentEl, options);
        }
        else {
            hideComponent(componentEl, options);
        }
    }
    /**
     * Directive that adds event listeners for responding to changes to the AEM layout.
     */
    const aemLayoutListenerDirective = {
        beforeMount(el) {
            el.setAttribute('data-aem-layout-controller-listener', '');
        },
        unmounted(el) {
            el.removeAttribute('data-aem-layout-controller-listener');
        }
    };
    // Build the plugin object.
    const aemLayoutController = {
        hideComponent,
        showComponent,
        toggleComponent,
        install(app) {
            app.directive('aem-layout-listener', aemLayoutListenerDirective);
            app.provide(injection_symbols_1.aemLayoutControllerKey, aemLayoutController);
        }
    };
    return aemLayoutController;
}
exports.createAemLayoutController = createAemLayoutController;
/**
 * This controller is created on the fly by the use API, so it has a reference to the
 * current component's lifecycle.
 */
function createAemLayoutLocalController(options) {
    const instance = (0, vue_1.getCurrentInstance)();
    const { aemLayoutController } = options;
    /**
     * Hides the component that calls this method, and also hides the parent AEM container
     * if it is the only visible component in that container.
     */
    function hideComponent(options) {
        if (instance === null || instance === void 0 ? void 0 : instance.vnode.el) {
            aemLayoutController.hideComponent(instance.vnode.el, options);
        }
    }
    /**
     * Shows the component that calls this method, and shows its parent AEM container if it is hidden.
     */
    function showComponent(options) {
        if (instance === null || instance === void 0 ? void 0 : instance.vnode.el) {
            aemLayoutController.showComponent(instance.vnode.el, options);
        }
    }
    /**
     * Toggles the visibility of the component that calls this method,
     * and shows or hides its parent AEM container likewise.
     */
    function toggleComponent(options) {
        if (instance === null || instance === void 0 ? void 0 : instance.vnode.el) {
            aemLayoutController.toggleComponent(instance.vnode.el, options);
        }
    }
    // Build the plugin object.
    const aemLayoutLocalController = {
        hideComponent,
        showComponent,
        toggleComponent
    };
    return aemLayoutLocalController;
}
exports.createAemLayoutLocalController = createAemLayoutLocalController;
//# sourceMappingURL=aem-layout-controller.js.map